import '@fontsource/golos-text/400.css';
import '@fontsource/golos-text/500.css';
import '@fontsource/golos-text/600.css';
import '@fontsource/unbounded/500.css';
import '@fontsource/unbounded/600.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import './dashboard.css';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { useSession } from '../session';
import { Costs } from './Costs';
import { buildSeries, LedgerFilter, Meta, MonthlyRow, Series, Toggles } from './data';
import { ml } from './fmt';
import { Ledger } from './Ledger';
import { Overview } from './Overview';

export const TABS = [
  ['overview', 'Обзор'],
  ['costs', 'Затраты'],
  ['ledger', 'Операции'],
] as const;
export type Tab = (typeof TABS)[number][0];

type Theme = 'light' | 'system' | 'dark';
const store = {
  get: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k: string, v: string) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* приватный режим: настройка не запомнится */
    }
  },
};

export interface Ctx {
  meta: Meta;
  series: Series;
  le: string | null;
  from: number;
  to: number;
  toggles: Toggles;
  /** Открыть операции с фильтром; back — вкладка, куда вернуться. */
  drill: (f: LedgerFilter, back: Tab) => void;
  theme: string;
}

/** Дашборд собственника: свой экран без меню МИС, данные — /api/dashboard (только owner). */
/**
 * standalone — открыт сам по себе (корень dash.<домен>): своя кнопка «Выйти».
 * misHref — где экраны МИС: «/mis/» на dash.<домен>, «/» на основном адресе.
 */
export function Dashboard({ standalone, misHref }: { standalone: boolean; misHref: string }) {
  const { me, logout } = useSession();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [rows, setRows] = useState<MonthlyRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [le, setLe] = useState<string | null>(() => store.get('dash-le') || null);
  const [period, setPeriod] = useState<[string, string] | null>(null);
  const [toggles, setToggles] = useState<Toggles>({ own: true, cap: true });
  const [tab, setTab] = useState<Tab>(() => (TABS.some(([k]) => `#${k}` === location.hash) ? (location.hash.slice(1) as Tab) : 'overview'));
  const [ledger, setLedger] = useState<{ f: LedgerFilter; back: Tab | null }>({ f: {}, back: null });
  const [theme, setTheme] = useState<Theme>(() => (store.get('gk-theme') as Theme) || 'system');

  useEffect(() => {
    api.get<Meta>('/dashboard/meta').then(setMeta, (e) => setError(e instanceof ApiError && e.status === 403
      ? 'Дашборд доступен только собственнику.'
      : (e as Error).message));
  }, []);
  useEffect(() => {
    if (!meta) return;
    setRows(null);
    const known = le && meta.legal_entities.some((l) => l.id === le) ? le : null;
    api.get<{ rows: MonthlyRow[] }>(`/dashboard/monthly${known ? `?le=${known}` : ''}`).then((r) => setRows(r.rows), (e) => setError((e as Error).message));
  }, [meta, le]);

  const series = useMemo(() => (meta && rows ? buildSeries(meta, rows) : null), [meta, rows]);
  const M = series?.months ?? [];
  // период по умолчанию — с января года последнего месяца
  const [from, to] = useMemo(() => {
    if (!M.length) return [0, 0];
    const last = M.length - 1;
    const p = period ?? [`${M[last].slice(0, 4)}-01`, M[last]];
    const a = Math.max(0, M.indexOf(p[0]) >= 0 ? M.indexOf(p[0]) : 0);
    const b = M.indexOf(p[1]) >= 0 ? M.indexOf(p[1]) : last;
    return [Math.min(a, b), b];
  }, [M, period]);

  const go = useCallback((t: Tab) => {
    setTab(t);
    try {
      history.replaceState(null, '', `#${t}`);
    } catch {
      /* без истории — вкладка не запомнится */
    }
    window.scrollTo(0, 0);
  }, []);
  const drill = useCallback((f: LedgerFilter, back: Tab) => {
    setLedger({ f, back });
    go('ledger');
  }, [go]);

  const chooseTheme = (t: Theme) => {
    setTheme(t);
    store.set('gk-theme', t);
  };
  const chooseLe = (v: string) => {
    setLe(v || null);
    store.set('dash-le', v);
  };

  const dataTheme = theme === 'system' ? undefined : theme;
  const shell = (body: React.ReactNode) => (
    <div className="dash" data-theme={dataTheme} ref={rootRef}>
      {body}
    </div>
  );

  if (error) {
    return shell(
      <div className="center">
        <div>
          <p>{error}</p>
          <p>
            {me?.name} · <a href={misHref}>Перейти в МИС</a>
            {standalone && (
              <>
                {' '}· <button type="button" className="linkbtn" onClick={() => void logout()}>Войти под другим пользователем</button>
              </>
            )}
          </p>
        </div>
      </div>,
    );
  }
  if (!meta || !series) return shell(<div className="center">Загружаю данные…</div>);
  if (!meta.first_month) return shell(<div className="center">Операций пока нет: дашборд заполнится после первой загрузки ДДС.</div>);

  const N = M.length;
  const lastYear = M[N - 1].slice(0, 4);
  const presets: [string, string, string][] = [
    [lastYear, `${lastYear}-01`, M[N - 1]],
    ['12 мес', M[Math.max(0, N - 12)], M[N - 1]],
    [String(+lastYear - 1), `${+lastYear - 1}-01`, `${+lastYear - 1}-12`],
    ['Всё', M[0], M[N - 1]],
  ];
  const ctx: Ctx = { meta, series, le, from, to, toggles, drill, theme };
  const stale = meta.sources.filter((s) => s.error || s.errors > 0);

  return shell(
    <>
      <header className="bar">
        <div className="wrap bar-in">
          <div className="brand">
            {meta.clinic} <small>ДДС, ОПУ, РНП</small>
          </div>
          <div className="ctl">
            <select aria-label="ООО" value={le ?? ''} onChange={(e) => chooseLe(e.target.value)}>
              <option value="">Все ООО</option>
              {meta.legal_entities.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>
          <div className="ctl">
            <span>Период</span>
            <select aria-label="Начало периода" value={from} onChange={(e) => setPeriod([M[Math.min(+e.target.value, to)], M[to]])}>
              {M.map((m, i) => <option key={m} value={i}>{ml(m)}</option>)}
            </select>
            <span>—</span>
            <select aria-label="Конец периода" value={to} onChange={(e) => setPeriod([M[from], M[Math.max(+e.target.value, from)]])}>
              {M.map((m, i) => <option key={m} value={i}>{ml(m)}</option>)}
            </select>
          </div>
          <div className="ctl">
            {presets.map(([t, a, b]) => (
              <button key={t} type="button" className={`chip${M[from] === a && M[to] === b ? ' on' : ''}`} onClick={() => setPeriod([a, b])}>
                {t}
              </button>
            ))}
          </div>
          <div className="ctl">
            <label className="tog">
              <input type="checkbox" checked={toggles.own} onChange={(e) => setToggles({ ...toggles, own: e.target.checked })} /> выплаты собственнику не расход
            </label>
            <label className="tog">
              <input type="checkbox" checked={toggles.cap} onChange={(e) => setToggles({ ...toggles, cap: e.target.checked })} /> CAPEX не расход
            </label>
          </div>
          <div className="seg" role="group" aria-label="Тема">
            {(['light', 'system', 'dark'] as Theme[]).map((t) => (
              <button key={t} type="button" className={theme === t ? 'on' : ''} onClick={() => chooseTheme(t)}>
                {t === 'light' ? 'Светлая' : t === 'system' ? 'Авто' : 'Тёмная'}
              </button>
            ))}
          </div>
          <a className="linkbtn" href={misHref}>МИС</a>
          {standalone && <button type="button" className="linkbtn" onClick={() => void logout()}>Выйти</button>}
        </div>
        <div className="wrap">
          <nav className="tabs" role="tablist" aria-label="Разделы">
            {TABS.map(([k, t]) => (
              <button key={k} type="button" role="tab" aria-selected={k === tab} className={k === tab ? 'on' : ''} onClick={() => go(k)}>
                {t}
              </button>
            ))}
          </nav>
        </div>
      </header>
      <main className="wrap" key={`${le}|${theme}`}>
        {stale.length > 0 && (
          <div className="warnbar" role="status">
            Загрузки с ошибками: {stale.map((s) => s.source).join(', ')}. Цифры за последние дни могут быть неполными — подробности в МИС, раздел «Источники».
          </div>
        )}
        {tab === 'overview' && <Overview ctx={ctx} />}
        {tab === 'costs' && <Costs ctx={ctx} />}
        {tab === 'ledger' && (
          <Ledger
            ctx={ctx}
            initial={ledger.f}
            back={ledger.back}
            onBack={() => ledger.back && go(ledger.back)}
            key={JSON.stringify(ledger)}
          />
        )}
      </main>
    </>,
  );
}
